import React, { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Sparkle } from '@phosphor-icons/react';

const InflationSimulator = () => {
  const [inflationRate, setInflationRate] = useState(3);
  const [currentSpending, setCurrentSpending] = useState(5000);
  const [years, setYears] = useState(30);

  const calculateInflationData = () => {
    const data = [];
    for (let year = 0; year <= years; year++) {
      const futureValue = currentSpending * Math.pow(1 + inflationRate / 100, year);
      const purchasingPower = (currentSpending / futureValue) * 100;
      data.push({
        year: year,
        futureSpending: Math.round(futureValue),
        purchasingPower: Math.round(purchasingPower)
      });
    }
    return data;
  };

  const chartData = calculateInflationData();
  const finalYear = chartData[chartData.length - 1];

  return (
    <div className="space-y-8 fade-in">
      {/* Educational Disclaimer */}
      <div className="bg-[#C5A059]/10 border border-[#C5A059]/30 rounded-md p-4">
        <div className="flex items-start gap-3">
          <Sparkle size={20} weight="duotone" className="text-[#C5A059] flex-shrink-0 mt-0.5" />
          <p className="text-sm text-[#4A5568]">
            <strong className="text-[#1A202C]">Educational Tool:</strong> This simulator provides general estimates. Actual inflation varies. Consult a financial professional for personalized planning.
          </p>
        </div>
      </div>

      <div>
        <h1 className="cabinet-grotesk text-3xl sm:text-4xl tracking-tight font-medium text-[#0A1128] mb-2">
          Inflation Simulator
        </h1>
        <p className="text-base text-[#4A5568]">
          Understand how inflation impacts your future purchasing power
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Controls */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card" data-testid="inflation-controls">
            <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-6">Assumptions</h2>
            
            <div className="space-y-6">
              <div>
                <Label htmlFor="current_spending" className="mb-2">Current Monthly Spending ($)</Label>
                <Input
                  id="current_spending"
                  type="number"
                  data-testid="current-spending-input"
                  value={currentSpending}
                  onChange={(e) => setCurrentSpending(Number(e.target.value))}
                  className="border-slate-300"
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-3">
                  <Label>Annual Inflation Rate</Label>
                  <span className="text-lg font-semibold text-[#C5A059] ibm-plex">{inflationRate}%</span>
                </div>
                <Slider
                  value={[inflationRate]}
                  onValueChange={(value) => setInflationRate(value[0])}
                  min={0}
                  max={10}
                  step={0.1}
                  data-testid="inflation-rate-slider"
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-[#4A5568] mt-1">
                  <span>0%</span>
                  <span>10%</span>
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-3">
                  <Label>Planning Horizon (Years)</Label>
                  <span className="text-lg font-semibold text-[#C5A059] ibm-plex">{years}</span>
                </div>
                <Slider
                  value={[years]}
                  onValueChange={(value) => setYears(value[0])}
                  min={5}
                  max={50}
                  step={1}
                  data-testid="years-slider"
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-[#4A5568] mt-1">
                  <span>5</span>
                  <span>50</span>
                </div>
              </div>

              {/* Summary */}
              <div className="pt-4 border-t border-slate-200">
                <h3 className="text-sm font-semibold uppercase tracking-[0.1em] text-[#4A5568] mb-3">Projection</h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#4A5568]">Today's Spending</span>
                    <span className="font-medium text-[#1A202C] ibm-plex">${currentSpending.toLocaleString()}/mo</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#4A5568]">In {years} Years</span>
                    <span className="font-medium text-[#1A202C] ibm-plex">${finalYear.futureSpending.toLocaleString()}/mo</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#4A5568]">Purchasing Power Loss</span>
                    <span className="font-medium text-[#C53030] ibm-plex">{(100 - finalYear.purchasingPower).toFixed(0)}%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Charts */}
        <div className="lg:col-span-2 space-y-6">
          {/* Future Spending Chart */}
          <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card" data-testid="future-spending-chart">
            <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-4">Future Spending Needs</h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <defs>
                  <linearGradient id="spendingGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#C5A059" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#C5A059" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="year" stroke="#4A5568" label={{ value: 'Years', position: 'insideBottom', offset: -5 }} />
                <YAxis stroke="#4A5568" label={{ value: 'Monthly Spending ($)', angle: -90, position: 'insideLeft' }} />
                <Tooltip />
                <Line type="monotone" dataKey="futureSpending" stroke="#C5A059" strokeWidth={3} dot={false} fill="url(#spendingGradient)" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Purchasing Power Chart */}
          <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card" data-testid="purchasing-power-chart">
            <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-4">Purchasing Power Decline</h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <defs>
                  <linearGradient id="powerGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#C53030" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#C53030" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="year" stroke="#4A5568" label={{ value: 'Years', position: 'insideBottom', offset: -5 }} />
                <YAxis stroke="#4A5568" label={{ value: 'Purchasing Power (%)', angle: -90, position: 'insideLeft' }} />
                <Tooltip />
                <Line type="monotone" dataKey="purchasingPower" stroke="#C53030" strokeWidth={3} dot={false} fill="url(#powerGradient)" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InflationSimulator;